import { Box, Card, CardContent, Grid, Typography } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import moment from "moment";
import { useDataProvider } from "ra-core";
import { Labeled } from "ra-ui-materialui";
import React, { useEffect, useState } from "react";
import { Booking, Report } from "../../types";
import { convertIsoToDateTime } from "../../utils/date";

const findUser = async (dataProvider: any, userId: string | number) => {
  if (!userId) return null;
  const userEndpoints = ["admins", "consultants", "drivers"];
  for (const endpoint of userEndpoints) {
    try {
      const { data } = await dataProvider.getOne(endpoint, { id: userId });
      if (data) {
        return data.full_name || data.user?.email || null;
      }
    } catch (error) {
      // Not found, try next endpoint
    }
  }
  return null;
};

const ReportItems: React.FC<{ record?: Booking }> = ({ record }) => {
  const dataProvider = useDataProvider();
  const [reportedBy, setReportedBy] = useState<string>("-");

  useEffect(() => {
    const fetchReportedBy = async () => {
      const userId = record?.reported_by;
      if (!userId) return;
      try {
        const userName = await findUser(dataProvider, userId);
        setReportedBy(userName || "-");
      } catch (error) {
        setReportedBy("-");
      }
    };
    fetchReportedBy();
  }, [record, dataProvider]);

  if (!record) return <>Loading...</>;

  // Check if there are any reports
  const hasReports = record.reports && record.reports.length > 0;

  if (!record.reported_by && !hasReports) {
    return (
      <Box display="flex" width="100%" height="100px" alignItems="center">
        <Box>There's no report uploaded to this booking yet.</Box>
      </Box>
    );
  }

  // Get surgery dates for mapping
  const surgeryDates =
    record.dates && Array.isArray(record.dates)
      ? record.dates
      : record.date
        ? [{ id: record.date, date: record.date }]
        : [];

  // Use reports array directly - each report now contains its own booking_invoices
  const reports = record.reports || [];

  // Find surgery date info by ID
  const getSurgeryDateInfo = (report: Report) => {
    const dateId = report.booking_date_id;

    if (!dateId) {
      return {
        label: "Report",
        date: report.created_at || record.report_date || record.date,
      };
    }

    const surgeryDate = surgeryDates.find((d: any) => {
      const id = typeof d === "object" ? d.id : d;
      return String(id) === String(dateId);
    });

    if (surgeryDate) {
      const dateValue =
        typeof surgeryDate === "string" ? surgeryDate : surgeryDate.date;
      const caseIndex = surgeryDates.findIndex((d: any) => {
        const id = typeof d === "object" ? d.id : d;
        return String(id) === String(dateId);
      });
      return {
        label: `Case ${caseIndex + 1}`,
        date: dateValue,
      };
    }

    return {
      label: "Unknown Case",
      date: report.created_at || null,
    };
  };

  const columns = [
    { field: "invoice_number", headerName: "Invoice Number", flex: 1 },
    { field: "total_amount", headerName: "Total Amount", flex: 1 },
    { field: "description", headerName: "Description", flex: 1 },
  ];

  return (
    <Box sx={{ flexGrow: 1 }} mt={3}>
      {/* General Information */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={6}>
          <Labeled label="Consultant ID">
            <span>{record.consultant?.id ?? "-"}</span>
          </Labeled>
          <Labeled label="Consultant">
            <span>{record.consultant?.full_name ?? "-"}</span>
          </Labeled>
          <Labeled label="Reported By">
            <span>{reportedBy}</span>
          </Labeled>
          {record?.invoice_number ? (
            <Labeled label="Invoice Number">
              <span>{record?.invoice_number}</span>
            </Labeled>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>

      {/* Reports grouped by surgery date */}
      {reports.map((report: Report, index: number) => {
        const dateInfo = getSurgeryDateInfo(report);

        // Get invoices from the report's booking_invoices array
        const invoices = report.booking_invoices || [];

        const rows = invoices.map((invoice: any, idx: number) => ({
          id: idx,
          invoice_number: invoice.invoice_number || "-",
          total_amount:
            new Intl.NumberFormat("en-SG", {
              style: "currency",
              currency: "SGD",
            }).format(invoice.total_amount) || "-",
          description: invoice.description || "-",
        }));

        return (
          <Card
            key={index}
            style={{ marginBottom: 16, border: "1px solid #e0e0e0" }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {dateInfo.label}
                {dateInfo.date &&
                  ` - ${moment(dateInfo.date).format("DD MMM YYYY, hh:mm A")}`}
              </Typography>

              {report.created_at && (
                <Labeled label="Report Date">
                  <span>
                    {`${convertIsoToDateTime(report.created_at)?.date}, ${
                      convertIsoToDateTime(report.created_at)?.time
                    }`}
                  </span>
                </Labeled>
              )}

              {report.note && (
                <Labeled label="Report Note">
                  <span>{report.note}</span>
                </Labeled>
              )}

              {/* Invoice Items Table */}
              {rows.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Items
                  </Typography>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    autoHeight
                    disableSelectionOnClick
                    hideFooter
                    disableColumnMenu
                    disableDensitySelector
                  />
                </Box>
              )}

              {/* Attachments */}
              {report.medias && report.medias.length > 0 && (
                <Box mt={2}>
                  <Labeled label="Attachments">
                    <ul>
                      {report.medias.map((media: any, counter: number) => (
                        <li key={counter}>
                          <a
                            href={media.url}
                            target="_blank"
                            rel="noreferrer"
                          >{`Attachment ${counter + 1}`}</a>
                        </li>
                      ))}
                    </ul>
                  </Labeled>
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};
export default ReportItems;
